/**
 * =========================================================================================
 * Pricing Plan - Plan Comparison (Step 2: Left Column)
 * =========================================================================================
 */

import React, { useMemo } from 'react';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import { numberWithComaToFixed } from 'ubs-framework/v2/common/function';
import { dateFormatter } from 'ubs-framework/v2/common/function';
import { BILLING_CYCLE, BILLING_CYCLE_OPTIONS, SUBSCRIPTION_STATUS } from '../../../../../../common/constant';
import { getBillingLabel, getDiscountForCycle, renderComparisonRow } from './utils';


const PlanComparison = React.memo(({
    selectedPlan,
    billingCycle,
    seatCount,
    totalEmpCount,
    previewData,
    hasSubscription,
    onGoBack,
    setPlanFetchCompleted,
    onBillingCycleChange,
    onSeatCountChange,
    _PI,
    subscriptionInfo,
    subscriptionStatus,
    currency
}) => {
    const changeType = previewData?.changes?.type;
    // An effective downgrade = proposed total is lower than current (regardless of changeType label).
    // COMBINED can flip direction depending on per-seat price difference, so always check the diff.
    const isEffectiveDowngrade = previewData?.diff?.totalAmount != null && previewData?.diff?.totalAmount < 0;
    const isPlanChanged = selectedPlan?._id !== _PI?.planId;
    const isCancelled = [SUBSCRIPTION_STATUS.CANCELLED, SUBSCRIPTION_STATUS.CUSTOMER_CANCELLED].includes(subscriptionStatus);
    const isUpgrade = (changeType === 'UPGRADE' || changeType === 'COMBINED');
    const isDowngrade = changeType === 'DOWNGRADE';

    const discountPercent = getDiscountForCycle(selectedPlan?.discount, billingCycle);
    const hasDiscount = discountPercent > 0;
    const discountedPrice = hasDiscount ? (selectedPlan.price * (100 - discountPercent) / 100)?.toFixed(2) : selectedPlan.price;

    const minSeats = useMemo(() => {
        const base = Math.max(selectedPlan.minEmployee, totalEmpCount);

        if (hasSubscription && subscriptionInfo?.seatCount && !isPlanChanged && !isCancelled) {
            return Math.max(selectedPlan.minEmployee, subscriptionInfo.seatCount + 1);
        }

        onSeatCountChange(base);
        setPlanFetchCompleted(true);

        return base;
    }, [hasSubscription, subscriptionInfo, isPlanChanged, isCancelled, selectedPlan, totalEmpCount]);

    return (
        <div className="u_pricing-plan__config">
            <div className="u_color-blue u_mb-20 u_small-font u_flex-align u_cp" onClick={onGoBack}>
                <Icon.ArrowLeftLine size={14} fill={COLOR.u_blue} className='u_mr-8' />
                Change plan
            </div>

            <div className="u_pricing-plan__config-plan u_mb-20">
                <div className="u_pricing-plan__config-plan-info u_flex u_flex-col">
                    <span className="u_pricing-plan__config-plan-name">{selectedPlan.title} Plan</span>
                    <p className="u_font">{selectedPlan.subtitle || ''}</p>
                </div>
                <div className="u_text-right">
                    <div className="u_pricing-plan__config-plan-amount u_color-blue">
                        {numberWithComaToFixed(discountedPrice, currency)}
                    </div>
                    <p className="u_font">
                        / {selectedPlan.billingText}
                    </p>
                    {hasDiscount && (
                        <div className="u_mt-4">
                            <span className="u_font u_color-font" style={{ textDecoration: 'line-through' }}>
                                {numberWithComaToFixed(selectedPlan.price, currency)}
                            </span>
                            <span className="u_font u_color-green u_fw-600 u_ml-4">
                                {discountPercent}% off
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Billing Cycle */}
            {_PI?.planId !== selectedPlan?._id && <div className="u_mt-20">
                <div className="u_small-font u_mb-14 u_fw-600">Billing Cycle</div>
                <div className="u_flex-align" style={{ gap: 0 }}>
                    {BILLING_CYCLE_OPTIONS.map((opt) => {
                        const tabDiscount = getDiscountForCycle(selectedPlan?.discount, opt.value);
                        return (
                            <div
                                key={opt.value}
                                className={`u_py-10 u_px-16 u_cp u_text-center u_small-font u_fw-600 u_b-light ${billingCycle === opt.value ? 'u_bg-lightblue u_color-blue' : 'u_bg-white u_color-font'}`}
                                style={{
                                    borderRadius: opt.value === BILLING_CYCLE.MONTHLY ? '10px 0 0 10px' : opt.value === BILLING_CYCLE.YEARLY ? '0 10px 10px 0' : '0',
                                    marginLeft: '-1px',
                                }}
                                onClick={() => onBillingCycleChange(opt.value)}
                            >
                                <span>{opt.label}</span>
                                {tabDiscount > 0 && (
                                    <span className="u_ml-4 u_font u_fw-700 u_color-green">-{tabDiscount}%</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>}

            {/* Seat Counter */}
            <div className="u_pricing-plan__seats u_mt-20 u_mb-0">

                <div className="u_pricing-plan__seats-label u_small-font u_mb-14 u_fw-600 u_flex-align">
                    Number of Users
                    <span className="u_small-font">(min {minSeats})</span>
                </div>
                <div className="u_pricing-plan__seats-counter u_flex-align">
                    <button
                        className="u_pricing-plan__seats-btn"
                        onClick={() => onSeatCountChange(Math.max(minSeats, seatCount - 1))}
                        disabled={seatCount <= minSeats}
                    >
                        <Icon.Minus size={16} />
                    </button>
                    <input
                        type="number"
                        className="u_pricing-plan__seats-input"
                        value={seatCount}
                        onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            onSeatCountChange(Math.max(minSeats, val));
                        }}
                        max={10000}
                        onKeyDown={e => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                    />
                    <button
                        className="u_pricing-plan__seats-btn"
                        onClick={() => onSeatCountChange(seatCount + 1)}
                    >
                        <Icon.Plus size={16} />
                    </button>
                </div>
                {seatCount < minSeats && (
                    <div className="u_font u_color-red u_mt-10">
                        Minimum {minSeats} users required
                    </div>
                )}

            </div>

            {/* Downgrade not supported */}
            {isEffectiveDowngrade && (
                <div className="u_mt-20 u_flex-align u_py-10 u_px-14 u_bg-red-1 u_radius-8">
                    <Icon.Warning size={14} fill="#FF6961" className="u_mr-6 u_flex-shrink" />
                    <p className="u_font u_color-red">
                        Your selected plan's monthly amount (
                        <strong>{numberWithComaToFixed(previewData?.proposed?.totalAmount, currency)}</strong>)
                        is lower than your current plan (
                        <strong>{numberWithComaToFixed(previewData?.current?.totalAmount, currency)}</strong>).
                        Please select a plan with a higher monthly amount.
                    </p>
                </div>
            )}

            {/* Current vs Modified Comparison */}
            {(previewData && hasSubscription) ? (
                <div className="u_b-light u_border-radius-12 u_mt-20">
                    {/* Header with change type badge */}
                    <div className="u_flex-align u_content-between u_p-16 u_bb-light u_bg-thead">
                        <div className="u_flex-align">
                            <h4 className="u_semi-large-font u_fw-600">Plan Comparison</h4>
                            {previewData?.changes?.seatsChanged && previewData?.changes?.seatDiff !== 0 && (
                                <span className="u_font u_fw-600 u_ml-8 u_color-blue">
                                    ({previewData?.changes?.seatDiff > 0 ? '+' : ''}{previewData?.changes?.seatDiff} seats)
                                </span>
                            )}
                        </div>
                        <div className={`u_inline-flex u_align-center u_font u_fw-600 u_px-10 u_py-4 u_radius-8 ${isDowngrade ? 'u_bg-red-1 u_color-red' : isUpgrade ? 'u_bg-green-1 u_color-green' : 'u_bg-blue-1 u_color-blue'}`}>
                            {isDowngrade
                                ? <><Icon.DownArrow size={12} fill="#FF6961" className="u_mr-4" /> Downgrade</>
                                : isUpgrade
                                    ? <><Icon.UpArrow size={12} fill="#60B158" className="u_mr-4" /> Upgrade</>
                                    : changeType === 'CYCLE_CHANGE' ? 'Billing Change' : 'Plan Change'
                            }
                        </div>
                    </div>

                    <div className="u_flex-align u_bb-light u_py-10 u_px-16">
                        <span className="u_small-font u_fw-600 u_w-33"></span>
                        <span className="u_small-font u_fw-600 u_w-33">Current</span>
                        <span className="u_small-font u_fw-600 u_w-33">Modified</span>
                    </div>

                    {/* Comparison Rows */}
                    {renderComparisonRow('Plan', previewData?.current?.planCode, previewData?.proposed?.planCode)}
                    {renderComparisonRow('Seats', previewData?.current?.seatCount, previewData?.proposed?.seatCount)}
                    {renderComparisonRow('Per Seat', previewData?.current?.perSeatPrice, previewData?.proposed?.perSeatPrice, true, currency)}
                    {renderComparisonRow('Billing Cycle', getBillingLabel(previewData?.current?.billingCycle), getBillingLabel(previewData?.proposed?.billingCycle))}
                    {renderComparisonRow('Base Amount', previewData?.current?.baseAmountBeforeDiscount || previewData?.current?.baseAmount, previewData?.proposed?.baseAmountBeforeDiscount || previewData?.proposed?.baseAmount, true, currency)}
                    {(previewData?.current?.discountAmount > 0 || previewData?.proposed?.discountAmount > 0) && (
                        <div className="u_flex-align u_bb-light u_py-10 u_px-16">
                            <span className="u_small-font u_color-font u_w-33">Discount</span>
                            <span className="u_small-font u_w-33">
                                {previewData?.current?.discountAmount > 0
                                    ? <span className="u_color-green u_fw-600">- {numberWithComaToFixed(previewData.current.discountAmount, currency)} ({previewData.current.discountPercentage}%)</span>
                                    : <span className="u_color-font">-</span>
                                }
                            </span>
                            <span className="u_small-font u_fw-600 u_w-33">
                                {previewData?.proposed?.discountAmount > 0
                                    ? <span className="u_color-green u_fw-700">- {numberWithComaToFixed(previewData.proposed.discountAmount, currency)} ({previewData.proposed.discountPercentage}%)</span>
                                    : <span className="u_color-font">-</span>
                                }
                            </span>
                        </div>
                    )}
                    {renderComparisonRow('GST (' + (previewData?.current?.gstRate * 100 || 18) + '%)', previewData?.current?.gstAmount, previewData?.proposed?.gstAmount, true, currency)}
                    {renderComparisonRow('Total', previewData?.current?.totalAmount, previewData?.proposed?.totalAmount, true, currency)}

                    {/* Per-cycle Difference */}
                    {previewData?.diff?.totalAmount !== 0 && (
                        <div className="u_flex-align u_content-between u_px-16 u_py-12 u_bg-thead">
                            <span className="u_small-font u_fw-600">Per-cycle difference</span>
                            <span className={`u_small-font u_fw-700 ${previewData?.diff?.totalAmount > 0 ? 'u_color-green' : 'u_color-red'}`}>
                                {previewData?.diff?.totalAmount > 0 ? '+ ' : '- '}{numberWithComaToFixed(Math.abs(previewData?.diff?.totalAmount || 0), currency)}/{getBillingLabel(previewData?.proposed?.billingCycle)?.toLowerCase()}
                            </span>
                        </div>
                    )}

                    {/* Proration Details */}
                    {previewData?.proration && (
                        <div className="u_m-16 u_py-14 u_px-16 u_bg-lightblue u_radius-8">
                            <div className="u_flex-align u_color-blue u_small-font u_fw-600 u_mb-10">
                                <Icon.InfoTooltip size={18} fill={COLOR.u_blue} className="u_mr-6" />
                                Proration Details
                            </div>
                            <div className="u_flex-align u_content-between u_py-6">
                                <span className="u_font u_color-font">Prorated charge for remaining cycle days</span>
                            </div>
                            {previewData?.proration?.proratedCredit > 0 && (
                                <div className="u_flex-align u_content-between u_py-6">
                                    <span className="u_font">Credit (unused period)</span>
                                    <span className="u_font u_fw-600 u_color-green">- {numberWithComaToFixed(previewData?.proration?.proratedCredit, currency)}</span>
                                </div>
                            )}
                            {previewData?.proration?.proratedCharge > 0 && (
                                <div className="u_flex-align u_content-between u_py-6">
                                    <span className="u_font">Additional Charge</span>
                                    <span className="u_font u_fw-600 u_color-red">+ {numberWithComaToFixed(previewData?.proration?.proratedCharge, currency)}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Payment Summary */}
                    {previewData?.payment && (
                        <div className="u_m-16 u_py-14 u_px-16 u_bg-lightgreen u_radius-8">
                            <div className="u_flex-align u_small-font u_fw-600 u_mb-10 u_color-green">
                                <Icon.CheckMarkRound size={20} fill={COLOR.u_green} className="u_mr-6" />
                                Payment Summary
                            </div>
                            {previewData?.payment?.isImmediate && previewData?.payment?.immediateChargeAmount > 0 && (
                                <div className="u_flex-align u_content-between u_py-6">
                                    <span className="u_semi-large-font u_fw-600">Pay now</span>
                                    <span className="u_semi-large-font u_fw-700 u_color-blue">
                                        {numberWithComaToFixed(previewData?.payment?.immediateChargeAmount, currency)}
                                    </span>
                                </div>
                            )}
                            <div className="u_flex-align u_content-between u_py-6">
                                <span className="u_small-font u_color-font">From next cycle</span>
                                <span className="u_small-font u_fw-600">
                                    {numberWithComaToFixed(previewData?.payment?.nextCycleAmount || 0, currency)}/{getBillingLabel(previewData?.proposed?.billingCycle)?.toLowerCase()}
                                </span>
                            </div>
                            {previewData?.payment?.effectiveDate && (
                                <div className="u_flex-align u_py-6 u_mt-4">
                                    <Icon.Calendar size={14} fill={COLOR.u_blue} className="u_mr-6 u_flex-shrink" />
                                    <span className="u_font">
                                        {previewData?.payment?.isImmediate ? 'Effective' : 'Changes effective'} from <strong>{dateFormatter({ date: previewData?.payment?.effectiveDate, format: 'DD MMM, YYYY' })}</strong>
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Pending Modification Warning */}
                    {previewData?.hasPendingModification && (
                        <div className="u_mx-16 u_mb-16 u_flex-align u_py-10 u_px-14 u_bg-lightorange u_radius-8">
                            <Icon.Warning size={14} fill="#FFA700" className="u_mr-6 u_mt-2 u_flex-shrink" />
                            <span className="u_font">A plan change is already scheduled. Click Subscribe Now — you'll be prompted to cancel it or apply your new change directly.</span>
                        </div>
                    )}
                </div>
            ) : <></>}
        </div>
    );
});

export default PlanComparison;
